import TranscoderService, { ITranscodeOptions } from '@engoo/transcoder-service';
import Logger from 'app/services/logger';
import React, { createContext, FunctionComponent, useContext, useRef } from 'react';
import { assetBaseURL } from 'app/config/client';
import { isUrlOffAppSite } from 'app/utils/isUrlOffAppSite';

const Context = createContext<TranscoderService | undefined>(undefined);

interface IProviderProps {
  transcoderService?: TranscoderService;
}

export const TranscoderServiceProvider: FunctionComponent<IProviderProps> = ({ children, transcoderService }) => {
  return React.createElement(Context.Provider, { children, value: transcoderService });
};

export const useTranscoderService = (): TranscoderService | undefined => {
  return useContext(Context);
};

/**
 * Helper that falls back on returning the src if the transcoder service is unavailable.
 */
export const useTranscodedSource = (src: string, options: ITranscodeOptions): string => {
  const transcoderService = useTranscoderService();
  const warning = useRef(false);

  if (transcoderService) {
    return transcoderService.getTranscodedSrc(src, options);
  } else {
    if (warning.current === false && !transcoderService) {
      Logger.warn('No transcoder service found.');
      warning.current = true;
    }

    return src;
  }
};

/*
* Helper for generating a transcoding url for a static asset. The original source is returned
* if the asset isn't being fetched from an external source, namely an asset host.
*/
export const useTranscodedStaticAssetSrc = (assetSrc: string, options: ITranscodeOptions) => {
  const transcodedHeaderSrc = useTranscodedSource(assetSrc, options);

  // Only use transcoded src if the image is being pulled from the static assets server, in which case
  // the asset base url will be offsite
  return isUrlOffAppSite(assetBaseURL) ? transcodedHeaderSrc : assetSrc;
};
